<template>
  <div
    class="bundle-thumbnail-svg"
    :style="{ padding }"
  >
    <svg
      v-if="frameSpecs"
      class="gallery-wall-thumbnail-svg"
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      tabindex="-1"
      :viewBox="viewBox"
    >
      <template v-for="(frameSpec, frameKey) in frameSpecs">
        <RenderSvgFrame
          :id="`gallery-${id}-frame-${frameKey}`"
          :frameSpec="frameSpec"
          :x="frameSpec.offsetInBundle.leftInInches"
          :y="frameSpec.offsetInBundle.topInInches"
          :inBundle="{ frameKey }"
          :readonly="true"
          nestedSVG
        />
      </template>
    </svg>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import FrameSpec from '@framebridge/toolbox/FrameSpec';
import RenderSvgFrame from '@framebridge/toolbox/components/RenderSvg/RenderSvgFrame.vue';

// Cache built FrameSpecs so we don't rebuild them every time a bundle tile scrolls back into view.
const frameSpecCache = new WeakMap();

const imageTypes = [
  'croppedAndNormalized',
  'customerSpecified',
  'normalized',
  'original',
  'originalUnedited',
  'normalizedUnedited'
];
const prependHost = (url, host) => {
  return url.startsWith('http') || url.startsWith('data:') ? url : `${host}${url}`;
};
const prependHostToArtworkUrl = (artwork, host) => {
  return {
    dimensionsInPixels: artwork.dimensionsInPixels,
    url: prependHost(artwork.url, host)
  };
};
const prependHostToFrameSpecUrls = (frameSpec, host) => {
  return {
    ...frameSpec,
    artworks: frameSpec.artworks.map((artworkSpec) => ({
      ...artworkSpec,
      ...imageTypes.reduce((acc, imageType) => {
        const artwork = artworkSpec[imageType];
        return artwork
          ? {
            ...acc,
            [imageType]: prependHostToArtworkUrl(artwork, host)
          } : acc;
      }, {})
    })),
    moulding: {
      ...frameSpec.moulding,
      ...(frameSpec.moulding.url ? { url: prependHost(frameSpec.moulding.url, host) } : {})
    }
  };
};
const loadBundleSpec = async (bundleSpecNumber, joineryHost, authorizationToken) => {
  const authHeaders = authorizationToken ? { "Framebridge-Resource-Authorization-Token": authorizationToken } : {};
  const res = await axios.get(`${joineryHost}/api/v1/bundle_specifications/${bundleSpecNumber}`, { headers: authHeaders });
  return {
    ...res.data,
    authorizationToken: authorizationToken,
    components: res.data.components.map((component) => ({
      ...component,
      frameSpec: prependHostToFrameSpecUrls(component.frameSpec, joineryHost)
    }))
  };
};

// A preview image stands in for the sample artwork in the bundle's first opening. Its dimensions travel
// with the url because RenderSvgArtwork sizes the rendered image from them: keeping the sample artwork's
// dimensions letterboxes the preview inside the opening.
const injectPreviewImage = (bundleSpecData, previewImage) => {
  const [firstComponent, ...otherComponents] = bundleSpecData.components;
  const [firstArtwork, ...otherArtworks] = firstComponent?.frameSpec?.artworks || [];

  if (!firstArtwork) return bundleSpecData;

  return {
    ...bundleSpecData,
    components: [
      {
        ...firstComponent,
        frameSpec: {
          ...firstComponent.frameSpec,
          artworks: [
            {
              ...firstArtwork,
              croppedAndNormalized: previewImage,
              customerSpecified: previewImage
            },
            ...otherArtworks
          ]
        }
      },
      ...otherComponents
    ]
  };
};

// some framespecs in bundles apparently don't have white samples images.
// when showing a user upload in one spot display this blank white image in the others  
const WHITE_PLACEHOLDER = {
  url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3Crect fill='white' width='1' height='1'/%3E%3C/svg%3E",
  dimensionsInPixels: { width: 1, height: 1 }
};

const blankSampleArtwork = (bundleSpecData) => {
  return {
    ...bundleSpecData,
    components: bundleSpecData.components.map((component, componentIndex) => ({
      ...component,
      frameSpec: {
        ...component.frameSpec,
        artworks: component.frameSpec.artworks.map((artwork, artworkIndex) => {
          const isPreviewArtwork = componentIndex === 0 && artworkIndex === 0;
          if (isPreviewArtwork) return artwork;

          return {
            ...artwork,
            croppedAndNormalized: WHITE_PLACEHOLDER,
            customerSpecified: WHITE_PLACEHOLDER
          };
        })
      }
    }))
  };
};

const frameSpecsFromBundle = (bundleSpecData) => {
  return bundleSpecData.components.map((frame) => {
    return {
      ...frame.frameSpec,
      offsetInBundle: {
        leftInInches: frame.fromLeft,
        topInInches: frame.fromTop
      }
    };
  });
};
const initFrameSpecs = async (frameSpecs, joineryHost, usedFullMats) => {
  return await Promise.all(frameSpecs.map(async (frameSpec) => {
    const fs = await FrameSpec.build({
      frameSpecData: frameSpec,
      number: frameSpec.id,
      joineryHost,
      usedFullMats
    });

    return fs;
  }));
};

export default defineComponent({
  components: {
    RenderSvgFrame
  },
  props: {
    id: {
      type: String,
      required: true
    },
    bundleSpecNumber: {
      type: String,
      required: false
    },
    token: {
      type: String,
      required: false
    },
    bundleSpec: {
      type: Object,
      required: false
    },
    previewImage: {
      type: Object,
      required: false
    },
    padding: {
      type: String,
      default: '10%'
    },
    joineryHost: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      frameSpecs: null,
      bundleWidth: 0,
      bundleHeight: 0,
      xStart: 0
    };
  },
  computed: {
    viewBox () {
      return `${this.xStart}, 0, ${this.bundleWidth}, ${this.bundleHeight}`;
    }
  },
  methods: {
    getAllUsedMatNames (bundleSpecData) {
      const matNames = bundleSpecData.components.map(
        (component) => component.frameSpec.mats.map((mat) => mat.name)
      ).flat(2);

      return [...new Set(matNames)];
    }
  },
  async mounted () {
    const joineryHost = this.joineryHost || import.meta.env.joineryHost;
    const bundleSpecData = this.bundleSpec
      ? this.bundleSpec
      : this.bundleSpecNumber && this.token
        ? await loadBundleSpec(
          this.bundleSpecNumber,
          joineryHost,
          this.token
        )
        : null;

    if (bundleSpecData) {
      const usedMatNames = this.getAllUsedMatNames(bundleSpecData);
      const usedFullMats = await FrameSpec.getMatsByNames(usedMatNames, joineryHost);

      if (this.previewImage) {
        const cached = frameSpecCache.get(bundleSpecData);
        if (cached && cached.previewUrl === this.previewImage.url) {
          this.frameSpecs = cached.frameSpecs;
          this.bundleWidth = cached.bundleWidth;
          this.bundleHeight = cached.bundleHeight;
          this.xStart = cached.xStart;
          return;
        }
      }

      let previewSpecData = bundleSpecData;
      if (this.previewImage) {
        previewSpecData = injectPreviewImage(previewSpecData, this.previewImage);
        previewSpecData = blankSampleArtwork(previewSpecData);
      }
      const minimalistFrameSpecs = frameSpecsFromBundle({
        ...previewSpecData,
        components: previewSpecData.components.map((component) => ({
          ...component,
          frameSpec: prependHostToFrameSpecUrls(
            component.frameSpec,
            joineryHost
          )
        }))
      });
      this.frameSpecs = await initFrameSpecs(
        minimalistFrameSpecs,
        joineryHost,
        usedFullMats
      );

      const leftOffsets = bundleSpecData.components.map(x => x.fromLeft);
      const minLeft = Math.min(...leftOffsets);
      this.xStart = minLeft;

      const rightEdges = bundleSpecData.components.map(
        x => x.fromLeft + x.frameSpec?.moulding?.exterior?.widthInInches
      );

      this.bundleWidth = Math.max(...rightEdges) - minLeft;

      this.bundleHeight = Math.max(
        ...bundleSpecData.components.map((x) => x.fromTop + x.frameSpec?.moulding?.exterior?.heightInInches)
      );

      if (this.previewImage) {
        frameSpecCache.set(bundleSpecData, {
          frameSpecs: this.frameSpecs,
          bundleWidth: this.bundleWidth,
          bundleHeight: this.bundleHeight,
          xStart: this.xStart,
          previewUrl: this.previewImage.url
        });
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.bundle-thumbnail-svg {
  align-items: center;
  box-sizing: borderbox;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;

  .gallery-wall-thumbnail-svg {
    outline: none;
  }
}
</style>
